import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop'
import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { NbAccordionModule, NbActionsModule, NbButtonModule, NbCardModule, NbIconModule } from '@nebular/theme'
import { PersonaFactoryService } from '@personas/models/persona.model'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { FormElementValueComponent } from '../form-element-value/form-element-value.component'
import { MatIcon } from '@angular/material/icon'
/**
 * Component that holds multiple form element values
 */
@Component({
  selector: 'ngx-personas-form-elements-values',
  templateUrl: './form-elements-values.component.html',
  styleUrls: ['./form-elements-values.component.scss'],
  standalone: true,
  imports: [CommonModule, NbAccordionModule, NbCardModule, NbButtonModule, FormElementValueComponent, NbActionsModule, NbIconModule, MatIcon, CdkDrag, CdkDragHandle, CdkDropList]
})
export class FormElementsValuesComponent implements OnInit, OnDestroy {
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  /**
   * Array of element values controls
   */
  @Input()
  elementValues: UntypedFormArray
  /**
   * Event that indicates the changed values
   */
  @Output()
  elementValuesChange: EventEmitter<UntypedFormArray> = new EventEmitter<UntypedFormArray>()

  /**
   * Form
   */
  @Input()
  form
  /** random unique identifier used to prevent clashing */
  identifier = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
  /** Creates the component & injects it's dependencies */
  constructor(
    private fb: UntypedFormBuilder,
    private pf: PersonaFactoryService
  ) {}
  /** On destroy implementation */
  ngOnDestroy(): void {
    // Subscription cleaning
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /** bootstraps the component */
  ngOnInit() {
    this.elementValues.controls.forEach((control: UntypedFormGroup) => {
      this.attachNameChangeSubscription(control)
    })
  }

  /** delete the form element value form the array.
   * @param  {number} index the index of the element you want to delete.
   *
   */
  deleteFieldValue(index: number) {
    if (this.elementValues.value[index]['id']) {
      // TODO: remove the form value from backend.
    }
    this.elementValues.removeAt(index)
  }

  /** Adds new field value to the field values array.
   *
   */
  addFieldValue() {
    const languages: string[] = this.form['value']['options']['languages']
    const translationObject = {}
    languages.forEach((element) => {
      translationObject[element] = ''
    })
    const valueGroup = this.fb.group({
      name: ['', Validators.required],
      slug: [''],
      weight: [this.pf.generateWeight(this.elementValues.length, this.elementValues.length, this.elementValues.value)],
      options: this.fb.group({ translations: this.fb.group(translationObject) })
    })
    this.elementValues.push(valueGroup)
    this.attachNameChangeSubscription(valueGroup)
  }

  /**
   * Only auto-updates slugs for new personas (without an id) to preserve existing slugs
   * @param control FormGroup
   */
  attachNameChangeSubscription(control: UntypedFormGroup) {
    // Don't update slug if the parent persona has already been saved (has an id)
    if (control.get('id')?.value) return

    control.controls['name'].valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      value !== ''
        ? control.get('slug').setValue(value)
        : control.get('slug').setValue(value, {
            onlySelf: true,
            emitEvent: false
          })
    })
  }
  /**
   * Returns a list of current slugs
   */
  get slugs(): string[] {
    return this.elementValues.value.map((element) => element['slug'])
  }

  /** title of a formgroup
   * @param element FormGroup
   */
  title(element) {
    return element.value['name'] ? element.value['name'] : 'New field value'
  }
  /**
   * Indicates enter key was pressed, adds a new value with a delay of 100ms then focuses it after the delay
   * @param index group index
   */
  handleEnter(index) {
    if (index === this.elementValues.length - 1) {
      this.addFieldValue()
      setTimeout(() => {
        document.getElementById(this.identifier + (index + 1))?.focus()
      }, 100)
    }
  }

  /**
   * Element was dropped (DnD implementation)
   * @param event Drop event of a form group
   */
  drop(event: CdkDragDrop<UntypedFormGroup>) {
    if (this.elementValues) {
      moveItemInArray(this.elementValues.controls, event.previousIndex, event.currentIndex)
      this.resetFieldsWeight()
    }
  }

  /**
   * gives all controls a new weight value based on their current order
   */
  resetFieldsWeight() {
    this.elementValues.controls.forEach((item) => {
      item.value.weight = this.elementValues.controls.indexOf(item)
    })
    this.elementValues.value.sort((a, b) => {
      return a.weight < b.weight ? -1 : 1
    })
  }
}
